// components/RepostCard.js
import React from "react";
import MediaGrid from "../components/MediaGrid";

/* 🔗 LINKIFY — makes links in text blue + clickable.
   Matches https://…, http://…, www.… and bare domains like coins.metisel.com */
const LINK_REGEX = /(?:https?:\/\/|www\.)[^\s<]+|(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+(?:com|net|org|io|co|app|dev|xyz|info|me|ai|gh|uk|us|ng|edu|gov|tv|site|online|store|tech|biz|ly|to|cc)\b(?::\d{2,5})?(?:[/?#][^\s<]*)?/gi;

const splitLinks = (text) => {
  const out = [];
  let last = 0;
  let m;
  LINK_REGEX.lastIndex = 0;
  while ((m = LINK_REGEX.exec(text))) {
    const start = m.index;
    const isBare = !/^(?:https?:\/\/|www\.)/i.test(m[0]);
    // bare domain glued to an email / word (user@site.com) → not a link
    if (isBare && start > 0 && /[@\w.-]/.test(text[start - 1])) continue;
    // keep trailing punctuation out of the link
    const url = m[0].replace(/[.,;:!?)\]}'"]+$/, "");
    if (!url) continue;
    if (start > last) out.push({ text: text.slice(last, start) });
    out.push({ url });
    last = start + url.length;
    LINK_REGEX.lastIndex = last;
  }
  if (last < text.length) out.push({ text: text.slice(last) });
  return out;
};

const LinkifiedText = ({ text }) => {
  if (!text) return null;

  return splitLinks(String(text)).map((part, i) => {
    if (!part.url) return <React.Fragment key={i}>{part.text}</React.Fragment>;

    const href = /^https?:\/\//i.test(part.url) ? part.url : `https://${part.url}`;
    return (
      <a
        key={i}
        href={href}
        target="_blank"
        rel="noopener noreferrer nofollow"
        onClick={(e) => e.stopPropagation()} // don't trigger the parent's click
        className="text-blue-500 hover:underline break-all"
      >
        {part.url}
      </a>
    );
  });
};

// "Show more" cut must not land in the middle of a link
const sliceKeepingLinks = (text, limit) => {
  let end = limit;
  let pos = 0;
  for (const p of splitLinks(text)) {
    const len = (p.url ?? p.text).length;
    if (p.url && pos < end && pos + len > end) {
      end = pos + len;
      break;
    }
    pos += len;
  }
  return text.slice(0, end);
};

export default function RepostCard({
  post,
  navigate,
  location,
  setZoomSrc,
  setZoomType,
  setZoomOpen,
}) {
  if (!post?.isRepost || !post?.originalPost) return null;

  const handleNavigateToOriginal = (e) => {
    e.stopPropagation();
    navigate(location.pathname, {
      state: {
        view: "post",
        post: post.originalPost,
        fromScrollY: window.scrollY,
      },
    });
  };

  const LIMIT = 120;
  const originalText = post.originalPost.text || "";
  const isLong = originalText.length > LIMIT;
  const displayText = isLong ? sliceKeepingLinks(originalText, LIMIT).trimEnd() + "…" : originalText;

  return (
    <div
      onClick={handleNavigateToOriginal}
      className={[
        "mt-2 rounded-2xl overflow-hidden",
        "border border-neutral-200 dark:border-neutral-700",
        "bg-neutral-50 dark:bg-neutral-800/60",
        "hover:bg-neutral-100 dark:hover:bg-neutral-700/60",
        "active:bg-neutral-200 dark:active:bg-neutral-700",
        "transition-colors duration-100",
        "cursor-pointer touch-manipulation",
      ].join(" ")}
    >
      {/* CARD HEADER */}
      <div className="px-3 pt-3 pb-1">
        <div className="flex items-center gap-1.5 text-xs text-neutral-500 dark:text-neutral-400 mb-1.5">
          {post.isQuote ? (
            <>
              <span>✏️</span>
              <span>Quoted</span>
            </>
          ) : (
            <>
              <span>🔁</span>
              <span>Reposted</span>
            </>
          )}
        </div>

        {/* ORIGINAL AUTHOR */}
        <div className="flex items-center gap-1.5">
          {post.originalPost.author?.avatarUrl ? (
            <img
              src={post.originalPost.author.avatarUrl}
              alt={post.originalPost.author.username}
              className="w-4 h-4 rounded-full object-cover shrink-0"
            />
          ) : (
            <div className="w-4 h-4 rounded-full bg-neutral-300 dark:bg-neutral-600 shrink-0" />
          )}
          <span className="text-sm font-semibold truncate text-neutral-800 dark:text-neutral-200">
            {post.originalPost.author?.username || "Unknown"}
          </span>
          {post.originalPost.author?.username && (
            <span className="text-xs text-neutral-400 hidden xs:inline truncate">
              @{post.originalPost.author.username}
            </span>
          )}
        </div>
      </div>

      {/* ORIGINAL TEXT */}
      {originalText && (
        <div className="px-3 pb-2 text-sm text-neutral-700 dark:text-neutral-300 whitespace-pre-wrap break-words leading-relaxed">
          <LinkifiedText text={displayText} />
        </div>
      )}

      {/* ORIGINAL MEDIA */}
      {post.originalPost.media?.length > 0 && (
        <div className="px-0 pb-0">
          <MediaGrid
            media={post.originalPost.media}
            onClick={(m) => {
              if (setZoomSrc && m.type === "image") {
                setZoomSrc(m.url);
                setZoomType?.(m.type);
                setZoomOpen?.(true);
              } else {
                navigate(location.pathname, {
                  state: {
                    view: "post",
                    post: post.originalPost,
                    fromScrollY: window.scrollY,
                  },
                });
              }
            }}
          />
        </div>
      )}
    </div>
  );
}